import { CheckCircle } from '@mui/icons-material';
import { Grid, Typography, SvgIcon, Stack } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import * as React from 'react';
import GenButton from './GenButton';

const CustomDialog = styled(Dialog)(({ theme }) => ({
  '.MuiDialog-paper': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
    justifyContent: 'center',
  },
  '& .MuiDialogActions-root>:not(:first-of-type)': {
    marginLeft: theme.spacing(3),
  },
}));

function GenDialog(props) {
  const { onClose, selectedValue, open, title, children, actions, sx } = props;

  const handleClose = () => {
    onClose(selectedValue);
  };

  return (
    <CustomDialog
      onClose={handleClose}
      open={open}
      maxWidth="sm"
      fullWidth={true}
      sx={sx}
    >
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>{children}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </CustomDialog>
  );
}

GenDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  title: PropTypes.node,
  actions: PropTypes.node,
  children: PropTypes.node,
};

const Title = ({
  title,
  titleIcon,
  titleIconColor,
  titleSx,
  subTitle,
  titleVariant,
}) => (
  <Grid container direction={'column'} alignItems="center">
    <Grid item>
      <SvgIcon
        color={titleIconColor}
        sx={{ fontSize: '71px', ...titleSx }}
        component={titleIcon}
      ></SvgIcon>
    </Grid>
    <Stack textAlign={'center'} gap={1}>
      <Typography variant={titleVariant}>{title}</Typography>
      {subTitle && (
        <Typography variant="body2" color="text.secondary">
          {subTitle}
        </Typography>
      )}
    </Stack>
  </Grid>
);

export const GenConfirmationDialog = ({
  open,
  title,
  titleVariant = 'h4',
  titleIcon = CheckCircle,
  titleIconColor = 'success',
  subTitle,
  titleSx,
  disabled = false,
  onClose,
  closeText,
  closeStartIcon,
  closeEndIcon,
  closeSx,
  confirmText,
  confirmStartIcon,
  confirmEndIcon,
  confirmSx,
  confirmColor = 'primary',
  onConfirm,
  onRefuse,
  children,
  isLoading = false,
  sx,
}) => {
  const getActions = () => (
    <>
      <GenButton
        size="large"
        sx={{
          color: theme => theme.palette.text.primary,
          borderColor: theme => theme.palette.divider,
          ...closeSx,
        }}
        disabled={isLoading}
        text={closeText}
        onClick={onRefuse}
        startIcon={closeStartIcon}
        endIcon={closeEndIcon}
      />
      <GenButton
        autoFocus
        color={confirmColor}
        variant="contained"
        size="large"
        disabled={disabled}
        startIcon={confirmStartIcon}
        endIcon={confirmEndIcon}
        sx={confirmSx}
        text={confirmText}
        onClick={onConfirm}
        loading={isLoading}
      />
    </>
  );

  return (
    <GenDialog
      title={
        <Title
          title={title}
          titleIcon={titleIcon}
          titleIconColor={titleIconColor}
          titleSx={titleSx}
          subTitle={subTitle}
          titleVariant={titleVariant}
        />
      }
      open={open}
      onClose={onClose}
      actions={getActions()}
      sx={sx}
    >
      {children}
    </GenDialog>
  );
};

export const GenAlertDialog = ({
  open,
  title,
  titleIcon = CheckCircle,
  titleIconColor = 'success',
  titleVariant,
  titleSx,
  disabled = false,
  onClose,
  closeText,
  closeStartIcon,
  closeEndIcon,
  closeSx,
  children,
}) => {
  const getActions = () => (
    <>
      <GenButton
        size="large"
        sx={{
          color: theme => theme.palette.text.primary,
          borderColor: theme => theme.palette.divider,
          ...closeSx,
        }}
        disabled={disabled}
        text={closeText}
        onClick={onClose}
        startIcon={closeStartIcon}
        endIcon={closeEndIcon}
      />
    </>
  );
  return (
    <GenDialog
      title={
        <Title
          title={title}
          titleIcon={titleIcon}
          titleIconColor={titleIconColor}
          titleVariant={titleVariant}
          titleSx={titleSx}
        />
      }
      open={open}
      onClose={onClose}
      actions={getActions()}
    >
      {children}
    </GenDialog>
  );
};
